import gql from 'graphql-tag'
import { FetchResult } from '@apollo/client'
// @ts-expect-error
import flex from '@cybersource/flex-sdk-web'

import { CreditCardTypeInfo } from 'credit-card-type'
import { apolloClient } from 'lib/core/services/magento/client'

import { useCheckout } from '../../../modules/checkout/hooks/useCheckout'

type SetPaymentMethodOnCartMutation = (params: SetPaymentMethodParams) => Promise<FetchResult<any>>

interface SetPaymentMethodParams {
  code: string
  cardType: string
  cvv: string
  expDate: string
  flexToken: any
  cartId: string
}

export interface CreditCardData {
  number: string
  expMonth: string
  expYear: string
  cvv: string
  cardType: CreditCardTypeInfo[] | string
}

export interface TokenResponseSuccess {
  keyId: string
  token: string
  maskedPan: string
  cardType: string
  timestamp: number
  signedFields: string
  signature: string
  discoverableServices: object
  _embedded: object
  error: {
    responseStatus: object
    _links: object
  }
}

export const useCyberSource = () => {
  const { lockCheckout, unlockCheckout, cartId } = useCheckout()

  const setPaymentMethodOnCart: SetPaymentMethodOnCartMutation = async ({
    code,
    cardType,
    cvv,
    expDate,
    flexToken,
    cartId
  }) => {
    const query = `
      mutation {
        setPaymentMethodOnCart (
          input: {
            cart_id: "${cartId}", 
            payment_method: {
              code: "${code}", 
              chcybersource: {
                cardType: "${cardType}", 
                cvv: "${cvv}"
                extDate: "${expDate}"
                keyId: "${flexToken.keyId}"
                maskedPan: "${flexToken.maskedPan}"
                signature: "${flexToken.signature}"
                signedFields: "${flexToken.signedFields}"
                timestamp: ${flexToken.timestamp}
                token: "${flexToken.token}"
              }
            }
          }
        ) {
          cart {
            selected_payment_method {
              code
              title
            }
          }
        }
      }
    `
    return apolloClient.mutate({ mutation: gql`${query}` })
  }

  const fetchCyberSourceToken = async (cartId: string) => {
    const query = `
      mutation {
        getCheckoutConfig(cart_id: "${cartId}") {
          payment {
            code
            payment_information
          }
        }
      }
  `
    return apolloClient.mutate({ mutation: gql`${query}` })
  }

  const convertCardType = (cardTypeInfo: CreditCardTypeInfo | any) => {
    switch (cardTypeInfo.type) {
      case 'american-express':
        return '003'
      case 'mastercard':
        return '002'
      case 'visa':
        return '001'
      case 'discover':
        return '004'
      default:
        return '001'
    }
  }

  const fetchCyberSourceData = async (ccData: CreditCardData, responseHandler: (args: TokenResponseSuccess) => void) => {
    if (!cartId) {
      return
    }

    lockCheckout()
    let options = {}
    await fetchCyberSourceToken(cartId)
      .then(token => {
        const jwk = token.data.getCheckoutConfig.payment[0].payment_information
        const jwkJson = JSON.parse(token.data.getCheckoutConfig.payment[0].payment_information)
        try {
          if (!jwk) {
            throw new Error('Invalid Tokens')
          }
          ccData.cardType = convertCardType(ccData.cardType[0])
          options = {
            kid: jwkJson.kid,
            keystore: jwkJson,
            cardInfo: {
              cardNumber: ccData.number,
              cardType: ccData.cardType,
              cardExpirationMonth: ccData.expMonth,
              cardExpirationYear: `20${ccData.expYear}`
            },
            encryptionType: 'RsaOaep256'
          }
          flex.createToken(options, responseHandler)
        } catch (e) {
          unlockCheckout()
          throw e
        }
      })
      .catch((e: { message: string | undefined }) => {
        throw new Error(e.message)
      })
  }

  return {
    fetchCyberSourceData,
    setPaymentMethodOnCart
  }
}
